import { useEffect, useState } from "react";
import dayStyles from "./calendar.day.module.css";
import monthStyles from "./calendar.month.module.css";
import { DaysGroup } from "./DaysGroup";
import { EventService } from "../../services/events.api.client";
import { AbsenceService } from "../../services/absence.api.client";
import { Event, mapToEvent } from "../../models/calendar/Event.model";
import { Absence, mapToAbsence } from "../../models/calendar/Absence.model";
import { EventsContext } from "../../contexts/calendar/EventsContext.context";
import { AbsencesContext } from "../../contexts/calendar/AbsencesContext.context";
import { ModalEventContext, Options } from "../../contexts/calendar/ModalEventContext.context";
import { ModalEvento } from "../modal/ModalEvento.modal";
import { useConfig } from "../../contexts/ConfigContext";
import { useCompany } from "../../contexts/CompanyContext";
import { useCalendar } from "../../contexts/calendar/CalendarContext.context";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useHistory } from "react-router-dom";
import { DatePicker, DatePickerProps } from "antd";
import clsx from "clsx";
import CalendarHeader from "./CalendarHeader";

const AnimatePresenceAny = AnimatePresence as any;


type Props = {
    view: string;
    day: string;
    month: string;
    year: string;
}


export default function CalendarComponent({view, day, month, year}: Readonly<Props>) {
    const [currentDate, setCurrentDate] = useState(new Date(Number.parseInt(year),Number.parseInt(month),Number.parseInt(day)));
    const [events,setEvents] = useState<Event[]>([]);
    const [absences,setAbsences] = useState<Absence[]>([]);
    const {company} = useCompany();
    const {calendar} = useCalendar();
    const [isLoad, setIsLoad] = useState(false);
    const styles = (view == 'day') ? dayStyles : monthStyles;
    const {config} = useConfig();
    const [options, setOptions] = useState<Options>({} as Options);
    const location = useLocation(); 
    const history = useHistory();
    const [showDatePicker, setShowDatePicker] = useState(false);


    useEffect(()=>{
      if(!company.current.uid || !calendar.uid) return;

      const nowDate = new Date(Number.parseInt(year),Number.parseInt(month),Number.parseInt(day));
      setCurrentDate(nowDate);

      const lastDate = new Date(nowDate.getFullYear(), nowDate.getMonth() + 1, 0);
      const firstDate = new Date(nowDate.getFullYear(), nowDate.getMonth(), 1);

      const initDayWeek = `${firstDate.getFullYear()}-${(firstDate.getMonth()+1).toString().padStart(2,'0')}-${firstDate.getDate().toString().padStart(2,'0')}T00:00:00-05:00`;
      const endDayWeek =  `${lastDate.getFullYear()}-${(lastDate.getMonth()+1).toString().padStart(2,'0')}-${lastDate.getDate().toString().padStart(2,'0')}T23:59:00-05:00`;

      setIsLoad(true);
      Promise.all([
        EventService.getEvents(company.current.uid,calendar.uid,initDayWeek,endDayWeek),
        AbsenceService.getAbsences(company.current.uid,calendar.uid,initDayWeek,endDayWeek).catch(() => ({ absenceList: [] })),
      ]).then(([response, absenceRes]) => {
          const data: any[] = response.eventList || response;
          if(Array.isArray(data)){
            setEvents(data.map(e => mapToEvent(e) as Event));
          }
          const absenceList = absenceRes?.absenceList || absenceRes || [];
          setAbsences(Array.isArray(absenceList) ? absenceList.map(mapToAbsence) : []);
          setIsLoad(false);
        }).catch((err) => {
          console.error('Error fetching events:', err);
          setIsLoad(false);
        });
      setCurrentDate(new Date(Number.parseInt(year),Number.parseInt(month),Number.parseInt(day)));
      //console.log('Company: ', company);
    },[year,month,day,config.key,config.session,company.current.uid,calendar.uid]);


    let daysInMonth = 0;
    const firstDayOfMonth = new Date(Number.parseInt(year), Number.parseInt(month), 1).getDay();
    const firstDayOfWeek = new Date(currentDate);

    const days = [];
    let row = 0;
    if(view === 'week' || view === 'day') {
      daysInMonth = (view=='week')?7:1;
      
      if(view === 'week')firstDayOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      for (let i = 0; i < daysInMonth; i++) {
        const currentDate = new Date(firstDayOfWeek);
        currentDate.setDate(firstDayOfWeek.getDate() + i);
        days.push(currentDate.getDate());
      }
    }else if(view === 'month') {
      daysInMonth = new Date(Number.parseInt(year), Number.parseInt(month) + 1, 0).getDate();
      for (let i = 0; i < firstDayOfMonth; i++) {
        days.push(-1); // Días vacíos al inicio
      }
      for (let i = 1; i <= daysInMonth; i++) {
        days.push(i);
      }
      row = Math.ceil(days.length / 7);
    }
    
    const weekdays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

    const changeMonth = (increment:number) => {
      let newDate = new Date();
      if(location.pathname.includes("day")){
        const date = currentDate.getDate() + increment;
        newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), date);
        setCurrentDate(newDate);
      } 
      const day = newDate.getDate();
      const month = newDate.getMonth();
      const year = newDate.getFullYear();

      history.push(`/calendar/day/${year}/${month+1}/${day}`);
    };


    const handleDatePicker: DatePickerProps['onChange'] = (date) => {
        setShowDatePicker(false);
        if(!date) return;
        const newDate = new Date(date.get('year'), date.get('month'), date.get('date'));
        setCurrentDate(newDate);
        history.push(`/calendar/day/${newDate.getFullYear()}/${newDate.getMonth()+1}/${newDate.getDate()}`);
    };


    const monthNames = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    if(config.isMobile){
      return (
          <div className="w-full flex flex-col items-center">
            <div className="flex items-center justify-between w-full max-w-sm px-4 pt-3">
              <button onClick={() => changeMonth(-1)}>
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-math-lower"
                >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M19 18l-14 -6l14 -6" />
                </svg>
              </button>
              <h2 className="text-lg font-semibold">
                {currentDate?
                  !showDatePicker
                  ?
                      <button 
                      className={styles["name-date-title"]}
                      onClick={() => setShowDatePicker(true)}>
                          {currentDate.getDate()} {monthNames[currentDate.getMonth()]} de {currentDate.getFullYear()}
                      </button>
                  :
                      <DatePicker onChange={handleDatePicker} className={styles["date-picker"]} />
                  : <></>
                }
              </h2>
              <button onClick={() => changeMonth(1)}>
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-math-greater"
                >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M5 18l14 -6l-14 -6" />
                </svg>
              </button>
            </div>

            <div className={clsx(styles['calendar-container'],"relative w-full max-w-sm overflow-hidden mt-6")}>
                <AnimatePresenceAny mode="wait">
                  <motion.div
                    key={`${year}-${month}-${day}`}
                    initial={{ x: 200, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -200, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className={styles["calendar-grid"]}
                  >
                    <EventsContext.Provider value={{events,setEvents}}>
                    <AbsencesContext.Provider value={{absences,setAbsences}}>
                      <ModalEventContext.Provider value={{options,setOptions}}>
                        <div className={styles["calendar-days-grid"]} style={view=='month'?{gridTemplateRows: 'repeat('+row+', minmax(0, 1fr))'}:{}}>
                            {!isLoad&&<DaysGroup days={days} month={Number.parseInt(month)} year={Number.parseInt(year)} isLoad={isLoad} view={view}/>}
                        </div>
                        <ModalEvento/> 
                      </ModalEventContext.Provider>
                    </AbsencesContext.Provider>
                    </EventsContext.Provider>  
                  </motion.div>
                </AnimatePresenceAny>
              </div>
            </div>
      );
    }else{
      return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <CalendarHeader view={view} day={day} month={month} year={year} />
            <div className={styles['calendar-container']} style={{ padding: '0 24px 24px 24px', flexGrow: 1, margin: 0, height: 'calc(100vh - 120px)' }}>
                <div className={styles["calendar-grid"]}>
                  <EventsContext.Provider value={{events,setEvents}}>
                  <AbsencesContext.Provider value={{absences,setAbsences}}>
                    <ModalEventContext.Provider value={{options,setOptions}}>
                      <div className={styles["weekday-headers"]}>
                          { 
                            (view=='day')?<div key={weekdays[firstDayOfWeek.getDay()]} className={styles["weekday-header"]}>
                                  <b>{weekdays[firstDayOfWeek.getDay()]}</b>
                              </div>:
                              weekdays.map((day) => (
                              <div key={day} className={styles["weekday-header"]}>
                                <b>{day}</b>
                              </div>
                            ))
                          }
                      </div>
                      <div className={styles["calendar-days-grid"]} style={view=='month'?{gridTemplateRows: 'repeat('+row+', minmax(0, 1fr))'}:{}}>
                          {!isLoad&&<DaysGroup days={days} month={Number.parseInt(month)} year={Number.parseInt(year)} isLoad={isLoad} view={view}/>}
                      </div>
                      <ModalEvento/> 
                    </ModalEventContext.Provider>
                  </AbsencesContext.Provider>
                  </EventsContext.Provider>  
                </div>
            </div>
        </div>
      )
    }
}